<template>
  <div
    class="editor_wrapper editor-input"
    @keydown.esc.stop.prevent="onEsc"
    @keydown.enter="onEnter"
  >
    <editor-content
      class="editor"
      :editor="editor"
      :spellcheck="getSpellcheck.show"
    />
  </div>
</template>

<script setup>
import { EditorContent } from '@tiptap/vue-3';
import { computed, onBeforeUnmount, watch } from 'vue';
import { useStore } from 'vuex';
import { lowlight } from 'lowlight';
import arcscript from '@/arcscript/arcscript.js';
import { useRichEditor } from './useRichEditor.js';

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
  extensionOptions: {
    type: Object,
    default: () => ({
      bold: false,
      blockquote: false,
      codeBlockHighlight: null,
      customLink: false,
      customMention: false,
      dropEventHandler: false,
      hardbreak: false,
      history: false,
      horizontalRule: false,
      italic: false,
      placeholder: null,
      underline: false,
      toolbarOnSelect: false,
    }),
  },
  caret: {
    type: Object,
    default: null,
  },
  variableNames: {
    type: Function,
    default: () => [],
  },
});
const model = defineModel({ type: String, default: '' });
if (!model.value) {
  model.value = props.content;
}

const store = useStore();
const getSpellcheck = computed(
  () => store.getters['project/config/getSpellcheck']
);

const emit = defineEmits(['focus', 'blur', 'update', 'input', 'submit']);

lowlight.registerLanguage('arcscript', arcscript(props.variableNames));
const { editor, isContentEmpty, removeToolbar } = useRichEditor({
  options: props.extensionOptions,
  caret: props.caret,
  emit,
  content: props.content,
  onUpdated: content => {
    model.value = content;
  },
});

const getContent = () => {
  return editor.value.getHTML();
};

const setContent = content => {
  if (content) {
    editor.value.commands.setContent(content);
  } else {
    editor.value.commands.clearContent();
  }
};

const focus = () => {
  editor.value.commands.focus();
};

const getText = options => {
  return editor.value.getText(options);
};

const isSuggestionActive = () => {
  const state = editor.value?.commands?.isSuggestionActive?.();
  if (state) {
    return state;
  }

  return false;
};

defineExpose({
  focus,
  getContent,
  getText,
  removeToolbar,
  setContent,
  isContentEmpty,
  isSuggestionActive,
});

watch(
  () => props.content,
  newContent => {
    if (getContent() !== newContent) {
      setContent(newContent || '');
    }
  }
);

onBeforeUnmount(() => {
  emit('blur', getContent());
  editor.value.destroy();
});

function onEsc() {
  editor.value.commands.blur();
}

function onEnter(event) {
  if (event.ctrlKey || event.metaKey) {
    emit('submit');
  }
}
</script>

<style lang="scss">
.editor p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: hsla(0, 0%, 100%, 0.3);
  pointer-events: none;
  height: 0;
  white-space: normal;
  text-overflow: ellipsis;
}

.menubar {
  &.is-hidden {
    display: none;
  }
}
.editor .mention,
.editor a,
.editor a:visited {
  color: #db841e;
  display: inline-block;
  white-space: nowrap;
  cursor: pointer;
  user-select: text;

  &:hover {
    text-decoration: underline;
  }
}

.editor hr {
  margin: 17px 0 15px 0;
  background: #fff;
  opacity: 0.2;
  border: none;
  height: 2px;
}

.editor_wrapper {
  flex: 1;
  height: 100%;

  .editor {
    font-size: 14px;
    line-height: 1.5;
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 22px;
    flex: 1;

    .ProseMirror {
      flex: 1;
      height: 100%;
    }
  }

  p {
    margin: 15px 0 0 0;

    &:first-child {
      margin: 0;
    }
  }
}

.editor .ProseMirror-focused {
  cursor: text !important;
  a {
    cursor: text !important;
    text-decoration: none;
  }
}

.editor a,
.editor a:visited {
  color: #db841e;
  background-position: 1px 5px;
  background-repeat: no-repeat;
  padding-left: 18px;
  background-size: 12px;
  background-image: linear-gradient(transparent, transparent),
    url(/images/external-link_opt.svg);
  text-decoration: none;
}

.title a {
  background-size: 14px;
  background-position: 1px 4px;
  padding-left: 20px !important;
}

.mention.mention-board {
  background-position: left 5px;
  background-repeat: no-repeat;
  padding-left: 18px;
  background-size: 14px;
  background-image: linear-gradient(transparent, transparent),
    url(/images/board_opt.svg);
  text-decoration: none;
  white-space: nowrap;
}

.title .mention.mention-board {
  background-size: 16px;
  background-position: 1px 4px;
  padding-left: 22px !important;
}

.suggestion-list {
  //Firefox scrollbar
  scrollbar-color: #2e3333 transparent;
  scrollbar-width: thin;

  background: #252929;
  border-radius: 4px;
  box-shadow: 0 5px 5px rgba(0, 0, 0, 0.2);
  border: 1px solid #313838;
  max-height: 300px;
  max-width: 500px;
  height: auto;
  overflow: auto;
  min-width: 240px;

  .suggestion-list-category:not(:first-child) {
    border-top: 1px solid #485050;
  }

  .header {
    font-size: 11px;
    padding: 12px 10px;
    color: #fff;
    opacity: 0.3;
    font-weight: 400;
    letter-spacing: 1px;
  }

  &__no-results {
    padding: 0.2rem 0.5rem;
  }
  &__item {
    width: 100%;
    padding: 5px 10px;
    cursor: pointer;
    color: #9ba1a3;
    display: flex;
    align-items: center;
    justify-content: left;
    font-size: 13px;

    &:last-child {
      margin-bottom: 0;
    }

    &.is-selected {
      background-color: rgba(255, 255, 255, 0.2) !important;
      color: #fff;
    }

    &:hover {
      color: #fff;
      background-color: rgba(255, 255, 255, 0.1);
    }
    &.is-empty {
      opacity: 0.5;
    }

    .thumb {
      display: flex;
      align-items: center;
      justify-content: center;

      width: 22px;
      height: 22px;
      min-width: 22px;
      min-height: 22px;
      overflow: hidden;
      margin: 0 10px 0 0;

      img {
        border-radius: 999px;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .name {
      flex: 1 1 50%;
    }
  }
}

.tippy-box[data-theme~='dark'] {
  background-color: rgba(0, 0, 0, 1);
  padding: 0;
  font-size: 1rem;
  text-align: inherit;
  color: 255, 255, 255;
  border-radius: 5px;
  .tippy-backdrop {
    display: none;
  }
  .tippy-roundarrow {
    fill: rgba(37, 41, 41, 1);
  }
  .tippy-popper[data-popper-placement^='top'] & .tippy-arrow {
    border-top-color: rgba(37, 41, 41, 1);
  }
  .tippy-popper[data-popper-placement^='bottom'] & .tippy-arrow {
    border-bottom-color: rgba(37, 41, 41, 1);
  }
  .tippy-popper[data-popper-placement^='left'] & .tippy-arrow {
    border-left-color: rgba(37, 41, 41, 1);
  }
  .tippy-popper[data-popper-placement^='right'] & .tippy-arrow {
    border-right-color: rgba(37, 41, 41, 1);
  }
}

.menububble {
  position: absolute;
  display: flex;
  z-index: 20;
  background: rgb(116, 26, 26);
  border-radius: 5px;
  padding: 0.3rem;
  margin-bottom: 0.5rem;
  transform: translateX(-50%);
  visibility: hidden;
  opacity: 0;
  transition:
    opacity 0.2s,
    visibility 0.2s;

  &.is-active {
    opacity: 1;
    visibility: visible;
  }

  &__button {
    display: inline-flex;
    background: transparent;
    border: 0;
    color: #fff;
    padding: 0.2rem 0.5rem;
    margin-right: 0.2rem;
    border-radius: 3px;
    cursor: pointer;

    &:last-child {
      margin-right: 0;
    }

    &:hover {
      background-color: rgba(#fff, 0.1);
    }

    &.is-active {
      background-color: rgba(#fff, 0.2);
    }
  }

  &__form {
    display: flex;
    align-items: center;
  }

  &__input {
    font: inherit;
    border: none;
    background: transparent;
    color: #fff;
  }
}

// SCROLLBARS

.editor {
  scrollbar-color: #242829 transparent;
  scrollbar-width: thin;
}

.editor::-webkit-scrollbar-track {
  background-color: transparent;
  border: none;
}

.editor::-webkit-scrollbar {
  width: 15px;
  background-color: transparent;
}

.editor::-webkit-scrollbar-thumb {
  width: 10px;
  border-left: 5px solid rgba(0, 0, 0, 0);
  background-color: #242829;
  background-clip: padding-box;
}

.editor::-webkit-scrollbar-thumb:hover {
  background-color: #424a4c;
}

.tippy-box[data-theme~='arcweave'] {
  background-color: transparent;
  padding: 0;
  border-radius: none;
  .tippy-content {
    padding: 0;
  }
}

// Code highlight
@import '../RichTextCodeStyles.scss';
</style>
